/* eslint-disable lit/no-invalid-html */
/* eslint-disable lit/binding-positions */
/* eslint-disable no-use-before-define */
import { CSSResultGroup, LitElement, nothing, TemplateResult } from 'lit';
import { property } from 'lit/decorators.js';
import { ref } from 'lit/directives/ref.js';
import { styleMap } from 'lit/directives/style-map.js';
import { html, unsafeStatic } from 'lit/static-html.js';
import { Builder, createViewBuilder, generateBindings } from './builder.js';
import {
  clearEffects,
  clearState,
  executeEffects,
  setCurrentComponent,
  subscribe,
} from './hooks/index.js';
import { spread } from './utils/spread.js';
import { toDashCase } from './utils/to-dashcase.js';
import { Clazz, View, ViewAnimation } from './view.js';

export const cssPropMetadataKey = Symbol('cssProp');
export const slotMetadataKey = Symbol('slot');

export function view<T, U>(
  tag: string,
  options: ComponentOptionsWithCtorAndFactory<T, U>
): [U & { View: Clazz<T & View> }, Clazz<T & View>, Clazz<any>];

export function view<T>(
  tag: string,
  options: ComponentOptionsWithCtor<T>
): [BuilderFactory<T> & { View: Clazz<T & View> }, Clazz<T & View>, Clazz<any>];

export function view<T>(
  tag: string,
  options: ComponentOptions<T>
): [
  BuilderFactory<{ props: T }> & { View: Clazz<View> },
  Clazz<View>,
  Clazz<any>
];

export function view<T, V = BuilderFactory<T>>(
  tag: string,
  options:
    | ComponentOptions<T>
    | ComponentOptionsWithCtor<T>
    | ComponentOptionsWithCtorAndFactory<T, V>
) {
  const {
    template,
    slotTemplate,
    cssTemplate,
    cssProps,
    viewRoot,
    noShadow,
  } = options;
  let Props: Clazz<T> | undefined;
  let mapBuilder: ((_: BuilderFactory<T>) => V) | undefined;

  if (hasPropsCtor<T>(options)) {
    Props = options.Props;
  }
  if (hasFactory<T, V>(options)) {
    mapBuilder = options.mapBuilder;
  }

  let styles: CSSResultGroup | CSSResultGroup[] = [];
  if (cssTemplate) {
    styles = typeof cssTemplate === 'function' ? cssTemplate() : cssTemplate;
  }

  class ComponentClass extends LitElement {
    componentId = Symbol(tag);

    @property({ type: Object }) props!: T;
    @property({ type: Array }) animations: ViewAnimation[] = [];

    static styles = styles;

    render() {
      setCurrentComponent(this.componentId);
      const tpl = template.bind(this)(this.props);
      return processTemplate(tpl);
    }

    createRenderRoot() {
      if (noShadow) {
        return this;
      }
      return super.createRenderRoot();
    }

    firstUpdated() {
      subscribe(this.componentId, this);
    }

    updated(changedProperties: Map<string | number | symbol, unknown>) {
      executeEffects(this.componentId, this);

      if (changedProperties.has('animations')) {
        this.animations.forEach(animation => {
          this.animate(animation.keyframes, animation.options);
        });
      }
    }

    disconnectedCallback() {
      clearState(this.componentId);
      clearEffects(this.componentId);
      super.disconnectedCallback();
    }
  }
  customElements.define(tag, ComponentClass);

  const el = unsafeStatic(tag);

  class ComponentView extends View {
    props?: T = undefined;

    slotProps = new Map<keyof T, string>();

    viewRoot(element: Element) {
      if (viewRoot) {
        return viewRoot(element);
      }
      return super.viewRoot(element);
    }

    constructor() {
      super();
      if (Props) {
        const mixin = new Props();
        const props = Object.keys(mixin);
        props.forEach(key => {
          this[key as keyof this] = mixin[key as keyof T] as any;
          const slotName = (Reflect as any).getMetadata(
            slotMetadataKey,
            mixin,
            key
          );
          if (slotName) {
            this.slotProps.set(key as keyof T, slotName);
          }
        });
      }
    }

    getPropValue(key: string) {
      if (this.props) {
        return this.props[key as keyof T];
      }
      return this[key as keyof this];
    }

    get body(): TemplateResult<any> {
      const enventsBindings = generateBindings(this.events);

      return html`
        <${el}
          ${spread({ ...this.attrs, ...enventsBindings })}
          .view="${this}"
          style="${styleMap(this.styles)}"
          .props="${this.props ? this.props : this}"
          .animations="${this.animations}"
          ${ref(this.elementRef ?? (() => {}))}
        >
        ${
          slotTemplate
            ? processTemplate(
                slotTemplate(this.props ? this.props : (this as any))
              )
            : html``
        }
        ${[...this.slotProps.keys()].map(key => {
          const keyView = this.getPropValue(key as string);

          const name = this.slotProps.get(key);
          return processTemplate(keyView as any, v =>
            v.slot(!name || name === 'default' ? '' : name)
          );
        })}
        </${el}>
      `;
    }
  }

  const _cssProps = cssProps ?? new Map<string, string | string[]>();
  if (Props && (Reflect as any).getMetadata) {
    const mixin = new Props();
    Object.keys(mixin).forEach(key => {
      const propName: string | string[] = (Reflect as any).getMetadata(
        cssPropMetadataKey,
        mixin,
        key
      );
      if (propName) {
        _cssProps.set(key, propName);
      }
    });
  }

  // eslint-disable-next-line func-names
  let ViewFactory: any = function () {
    return createViewBuilder(ComponentView, {}, _cssProps) as any;
  };

  if (mapBuilder) {
    ViewFactory = mapBuilder(ViewFactory);
  }

  ViewFactory.View = ComponentView;

  return [ViewFactory, ComponentView, ComponentClass] as any;
}

export function cssPropsFrom(...props: string[]) {
  return new Map<string, string>(
    props.map(prop => [prop, `--${toDashCase(prop)}`])
  );
}

export function cssProp(name: string, attrName?: string) {
  if ((Reflect as any).metadata) {
    let value: string | string[] = name;
    if (attrName) {
      value = [name, attrName];
    }
    return (Reflect as any).metadata(cssPropMetadataKey, value);
  }
  return undefined;
}

export function slot(name = 'default') {
  if ((Reflect as any).metadata) {
    return (Reflect as any).metadata(slotMetadataKey, name);
  }
  return undefined;
}

function processTemplate(
  tpl: (TemplateResult<any> | View) | (TemplateResult<any> | View)[],
  viewPreprocessor: (_: View) => View = v => v
) {
  if (!tpl) {
    return nothing;
  }

  if (tpl instanceof View) {
    return viewPreprocessor(tpl).body;
  }

  if (Array.isArray(tpl)) {
    return tpl.map(t => (t instanceof View ? viewPreprocessor(t).body : t));
  }

  return tpl;
}

interface ComponentOptions<T> {
  template: (
    props: T
  ) => (TemplateResult<any> | View) | (TemplateResult<any> | View)[];
  slotTemplate?: (
    props: T
  ) => (TemplateResult | View) | (TemplateResult | View)[];
  cssTemplate?: (() => CSSResultGroup) | CSSResultGroup;
  cssProps?: Map<string, string>;
  viewRoot?: (element: Element) => Element;
  noShadow?: boolean;
}
interface ComponentOptionsWithCtor<T> extends ComponentOptions<T> {
  Props: Clazz<T>;
}

export type BuilderFactory<T> = () => Builder<T>;

interface ComponentOptionsWithCtorAndFactory<T, U>
  extends ComponentOptionsWithCtor<T> {
  mapBuilder: (_: BuilderFactory<T>) => U;
}

function hasPropsCtor<T>(options: any): options is ComponentOptionsWithCtor<T> {
  if ('Props' in options) {
    return true;
  }
  return false;
}

function hasFactory<T, U>(
  options: any
): options is ComponentOptionsWithCtorAndFactory<T, U> {
  return hasPropsCtor<T>(options) && 'mapBuilder' in options;
}
